import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import ScrollToTop from "@/components/layout/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import HomePage from "@/pages/HomePage";
import NotFoundPage from "@/pages/NotFoundPage";
import BedrijvengidsPage from "@/pages/BedrijvengidsPage";
import BedrijfDetailPage from "@/pages/BedrijfDetailPage";
import VoorOndernemersPage from "@/pages/VoorOndernemersPage";
import AanpassenPage from "@/pages/AanpassenPage";
import ColofonPage from "@/pages/ColofonPage";
import OverOnsPage from "@/pages/OverOnsPage";
import ContactPage from "@/pages/ContactPage";
import ZienEnDoenPage from "@/pages/ZienEnDoenPage";
import WatersportHavenPage from "@/pages/WatersportHavenPage";
import WoudagemaalPage from "@/pages/WoudagemaalPage";
import PraktischPage from "@/pages/PraktischPage";
import WebcamsPage from "@/pages/WebcamsPage";
import LedenPage from "@/pages/LedenPage";
import AgendaPage from "@/pages/AgendaPage";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminForgotPasswordPage from "@/pages/admin/AdminForgotPasswordPage";
import AdminResetPasswordPage from "@/pages/admin/AdminResetPasswordPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminSubmissionsPage from "@/pages/admin/AdminSubmissionsPage";
import AdminContactPage from "@/pages/admin/AdminContactPage";
import AdminListingsPage from "@/pages/admin/AdminListingsPage";
import AdminNewsPage from "@/pages/admin/AdminNewsPage";
import AdminEventsPage from "@/pages/admin/AdminEventsPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminDocumentsPage from "@/pages/admin/AdminDocumentsPage";
import AdminInviteAcceptPage from "@/pages/admin/AdminInviteAcceptPage";
import NieuwsPage from "@/pages/NieuwsPage";
import NieuwsDetailPage from "@/pages/NieuwsDetailPage";
import ProtectedAdminRoute from "@/components/admin/ProtectedAdminRoute";
import AdminShell from "@/components/admin/AdminShell";
import { LangProvider } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";
import "@/App.css";

const AdminProtected = ({ children }) => (
  <ProtectedAdminRoute>
    <AdminShell>{children}</AdminShell>
  </ProtectedAdminRoute>
);

const LangRoot = ({ lang }) => {
  const c = pickLang(pageContent.categoryIntros, lang);
  return (
    <LangProvider lang={lang}>
      <Layout>
        <ErrorBoundary>
          <Routes>
          <Route index element={<HomePage />} />
          <Route
            path="eten-en-drinken"
            element={
              <BedrijvengidsPage
                lockedCategory="eten-drinken"
                heading={c["eten-en-drinken"]?.heading}
                intro={c["eten-en-drinken"]?.intro}
              />
            }
          />
          <Route
            path="winkelen"
            element={
              <BedrijvengidsPage
                lockedCategory="winkelen"
                heading={c.winkelen?.heading}
                intro={c.winkelen?.intro}
              />
            }
          />
          <Route
            path="diensten"
            element={
              <BedrijvengidsPage
                lockedCategory="diensten"
                heading={c.diensten?.heading}
                intro={c.diensten?.intro}
              />
            }
          />
          <Route path="zien-en-doen" element={<ZienEnDoenPage />} />
          <Route path="woudagemaal" element={<WoudagemaalPage />} />
          <Route path="watersport-haven" element={<WatersportHavenPage />} />
          <Route path="watersport-en-haven" element={<WatersportHavenPage />} />
          <Route
            path="overnachten"
            element={
              <BedrijvengidsPage
                lockedCategory="overnachten"
                heading={c.overnachten?.title}
                intro={c.overnachten?.subtitle}
              />
            }
          />
          <Route
            path="mode"
            element={
              <BedrijvengidsPage
                lockedCategory="mode"
                heading={c.mode?.heading || "Mode"}
                intro={c.mode?.intro}
              />
            }
          />
          <Route
            path="wonen-interieur"
            element={
              <BedrijvengidsPage
                lockedCategory="wonen-interieur"
                heading={c["wonen-interieur"]?.heading || "Wonen & Interieur"}
                intro={c["wonen-interieur"]?.intro}
              />
            }
          />
          <Route
            path="gezondheid-beauty"
            element={
              <BedrijvengidsPage
                lockedCategory="gezondheid-beauty"
                heading={c["gezondheid-beauty"]?.heading || "Gezondheid & Beauty"}
                intro={c["gezondheid-beauty"]?.intro}
              />
            }
          />
          <Route
            path="cultuur-vrije-tijd"
            element={
              <BedrijvengidsPage
                lockedCategory="cultuur-vrije-tijd"
                heading={c["cultuur-vrije-tijd"]?.heading || "Cultuur & Vrije tijd"}
                intro={c["cultuur-vrije-tijd"]?.intro}
              />
            }
          />
          <Route path="agenda" element={<AgendaPage />} />
          <Route path="nieuws" element={<NieuwsPage />} />
          <Route path="nieuws/:slug" element={<NieuwsDetailPage />} />
          <Route path="webcams" element={<WebcamsPage />} />
          <Route path="leden" element={<LedenPage />} />
          <Route path="praktisch" element={<PraktischPage />} />
          <Route path="over-ons" element={<OverOnsPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route
            path="bedrijvengids"
            element={
              <BedrijvengidsPage
                heading={c.bedrijvengids?.heading}
                intro={c.bedrijvengids?.intro}
              />
            }
          />
          <Route path="bedrijf/:slug" element={<BedrijfDetailPage />} />
          <Route path="voor-ondernemers" element={<VoorOndernemersPage />} />
          <Route path="voor-ondernemers/aanmelden" element={<VoorOndernemersPage />} />
          <Route path="voor-ondernemers/aanpassen" element={<AanpassenPage />} />
          <Route path="colofon" element={<ColofonPage />} />
          <Route
            path="*"
            element={<NotFoundPage />}
          />
        </Routes>
        </ErrorBoundary>
      </Layout>
    </LangProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AdminAuthProvider>
        <Routes>
          {/* Admin routes (no public Layout, no lang prefix) */}
          <Route path="/beheer/login" element={<AdminLoginPage />} />
          <Route path="/beheer/wachtwoord-vergeten" element={<AdminForgotPasswordPage />} />
          <Route path="/beheer/wachtwoord-reset" element={<AdminResetPasswordPage />} />
          <Route path="/beheer/uitnodiging" element={<AdminInviteAcceptPage />} />
          <Route
            path="/beheer/gebruikers"
            element={
              <AdminProtected>
                <AdminUsersPage />
              </AdminProtected>
            }
          />
          <Route
            path="/beheer"
            element={
              <AdminProtected>
                <AdminDashboardPage />
              </AdminProtected>
            }
          />
          <Route
            path="/beheer/inzendingen"
            element={
              <AdminProtected>
                <AdminSubmissionsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/beheer/contact"
            element={
              <AdminProtected>
                <AdminContactPage />
              </AdminProtected>
            }
          />
          <Route
            path="/beheer/bedrijven"
            element={
              <AdminProtected>
                <AdminListingsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/beheer/nieuws"
            element={
              <AdminProtected>
                <AdminNewsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/beheer/agenda"
            element={
              <AdminProtected>
                <AdminEventsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/beheer/documenten"
            element={
              <AdminProtected>
                <AdminDocumentsPage />
              </AdminProtected>
            }
          />

          {/* Lang-prefixed public routes */}
          <Route path="/de/*" element={<LangRoot lang="de" />} />
          <Route path="/en/*" element={<LangRoot lang="en" />} />
          <Route path="/*" element={<LangRoot lang="nl" />} />
        </Routes>
      </AdminAuthProvider>
    </BrowserRouter>
  );
}

export default App;
